import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { BasicTableHead, BasicTableHeadProps } from '../../../../components/molecules';
import { formatJstDateJapanese, formatNumberToJaJpLocale } from '../../../../util';
import { TextFieldController } from '../../../../components/organisms/form/controllers/TextFieldController';
import { useCouponPackBulkStart } from '../hooks/coupon-pack-bulk-start-use-case';
import { useCouponPackBulkHistories } from '../hooks/coupon-pack-bulk-histories-use-case';
import { CouponMasterInPack, CouponPackMasterQuery } from '../../../../graphql/generated';
import { NumberFieldController } from '../../../../components/organisms/form/controllers/NumberFieldController';

const useStyles = makeStyles((theme: Theme) => ({
  histories: {
    marginTop: theme.spacing(3),
  },
}));

type BulkIssueFormValues = {
  issueNumber: number;
  description: string;
};

export type CouponPackMasterBulkTabProps = {
  couponPackMaster?: CouponPackMasterQuery['couponPackMaster'];
};

export const CouponPackMasterBulkTab: React.FC<CouponPackMasterBulkTabProps> = ({ couponPackMaster }) => {
  const styles = useStyles();
  const theme = useTheme();

  // Use custom hooks following BulkAndHistories pattern
  const bulkHistoryProps = useCouponPackBulkHistories(couponPackMaster?.id);
  const bulkStartProps = useCouponPackBulkStart(couponPackMaster, bulkHistoryProps.refetch);

  const bulkIssueForm = useForm<BulkIssueFormValues>();
  const { handleSubmit } = bulkIssueForm;

  // Compare dates with millisecond precision

  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'publishDateTime',
        label: '発行日',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'issuedNumber',
        label: '発行数',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'description',
        label: '発行メモ',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'download',
        label: 'CSVダウンロード',
        minWidth: theme.spacing(10),
        align: 'left',
      },
    ],
  };

  const getTotalQuantityOfProducts = (products?: CouponMasterInPack[]): number => {
    if (!products || !products.length) return 1;

    return products.reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  return (
    <>
      <Box>
        <FormProvider {...bulkIssueForm}>
          <form onSubmit={handleSubmit(bulkStartProps.onSubmit)} autoComplete="off" noValidate>
            <Paper style={{ padding: 24 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={12}>
                  <NumberFieldController
                    required
                    variant="outlined"
                    label="発行枚数"
                    size="small"
                    name="issueNumber"
                    disabled={bulkStartProps.loading}
                    isFormatWithComma
                    preventZero
                    rule={{
                      required: '発行枚数が入力されていません',
                      max: {
                        value: Math.floor(2000000 / getTotalQuantityOfProducts(couponPackMaster?.products)), // maximum issue number is 2000000 divided by total quantity of products in the pack
                        message: `発行上限は${formatNumberToJaJpLocale(
                          Math.floor(2000000 / getTotalQuantityOfProducts(couponPackMaster?.products))
                        )}です。`,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={12}>
                  <TextFieldController
                    variant="outlined"
                    size="small"
                    label="発行メモ（任意）"
                    name="description"
                    disabled={bulkStartProps.loading}
                    maxLength={100}
                    rule={{
                      maxLength: {
                        value: 100,
                        message: '発行メモは100文字以内の半角英数字です',
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Button
                    type="submit"
                    startIcon={bulkStartProps.loading ? <></> : <PublishIcon />}
                    variant="outlined"
                    size="medium"
                    disabled={bulkStartProps.loading}
                  >
                    {bulkStartProps.loading ? <CircularProgress color="inherit" size={26} /> : 'URLを発行する'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </form>
        </FormProvider>
      </Box>

      <Box className={styles.histories}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              {bulkHistoryProps.loading ? <LinearProgress /> : <React.Fragment />}
              <Table>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {(() => {
                    if (!bulkHistoryProps.data || !bulkHistoryProps.data.couponBulkHistories) {
                      return null;
                    } else if (
                      bulkHistoryProps.data.couponBulkHistories.nodes &&
                      bulkHistoryProps.data.couponBulkHistories.nodes.length === 0
                    ) {
                      return (
                        <TableRow>
                          <TableCell>データがありません</TableCell>
                        </TableRow>
                      );
                    } else {
                      return bulkHistoryProps.data.couponBulkHistories.nodes.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell component="th" scope="row">
                            {formatJstDateJapanese(row.publishDateTime)}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {formatNumberToJaJpLocale(row.issuedNumber || 0)}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {row.description}
                          </TableCell>
                          <TableCell scope="row">
                            <Button
                              variant={'outlined'}
                              onClick={() => bulkHistoryProps.onDownload(row)}
                              type="button"
                              size="small"
                              color="primary"
                              disabled={!!bulkHistoryProps.downloadInProgressId}
                            >
                              {bulkHistoryProps.downloadInProgressId === row.id ? '生成中...' : 'ダウンロード'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {bulkHistoryProps.data?.couponBulkHistories?.pageInfo?.nextToken ? (
            <Grid item xs={12}>
              <Grid container justifyContent={'center'}>
                <Button
                  size="large"
                  color="default"
                  variant="contained"
                  disabled={bulkHistoryProps.loading}
                  onClick={() => bulkHistoryProps.onFetchMore()}
                  fullWidth
                >
                  さらに読み込む
                </Button>
              </Grid>
              {bulkHistoryProps.loading ? <LinearProgress /> : <React.Fragment />}
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
      </Box>
    </>
  );
};
